import type { Enums, Tables } from "../database.types";
export type * from "./media-fetch-map";

/**
 * The junction tables the files API links attachments through, and the whole of them.
 *
 * `/files/:tableName/…` takes its table from the URL, so this list is the entire difference
 * between the fifteen collections the clients and the other domains write and every table in the
 * database whose name ends in `Files`. Two more are absent. `organizationWorksiteDocumentFiles` has
 * no parent column at all — a worksite document hangs off the organisation itself, and the
 * documents API is what serves it. `incomingInvoiceFiles` has a parent but no domain yet: nothing
 * reads or writes an incoming invoice through the API, so nothing may attach a file to one.
 *
 * The server's registry (`apps/server/src/handlers/files/registry.ts`) declares one entry per
 * value and does not compile without it, so a collection is added here and there together or not
 * at all. A value outside this list is a validation error, not a query.
 */
export const FILE_COLLECTION_TABLES = [
	"articleFiles",
	"noteFiles",
	"organizationServiceFiles",
	"outgoingInvoiceFiles",
	"partnerReportFiles",
	"projectDocumentFiles",
	"projectFiles",
	"protocolFiles",
	"returnOrderFiles",
	"serviceCalculationFiles",
	"serviceCalculationItemFiles",
	"suborderFiles",
	"ticketFiles",
	"vehicleDocumentFiles",
	"wikiPageFiles",
] as const;

export type FileCollectionTable = (typeof FILE_COLLECTION_TABLES)[number];

/**
 * The foreign key columns each collection may be addressed by.
 *
 * The table name is only half of what the unlink and link routes take from the caller: the other
 * half is the *column* the parent id is matched against, and a bare string there lets a caller
 * pick which column of the row a delete matches on. Every value is pinned here, so a pair the
 * list does not carry is refused before a statement exists.
 *
 * `ticketFiles` is the one collection with two: a ticket file row belongs to a ticket and to the
 * project the ticket sits on, and linking one writes both.
 */
export const FILE_COLLECTION_KEYS = {
	articleFiles: ["articleId"],
	noteFiles: ["noteId"],
	organizationServiceFiles: ["organizationServiceId"],
	outgoingInvoiceFiles: ["outgoingInvoiceId"],
	partnerReportFiles: ["partnerReportId"],
	projectDocumentFiles: ["projectId"],
	projectFiles: ["projectId"],
	protocolFiles: ["protocolId"],
	returnOrderFiles: ["returnOrderId"],
	serviceCalculationFiles: ["serviceCalculationId"],
	serviceCalculationItemFiles: ["serviceCalculationItemId"],
	suborderFiles: ["suborderId"],
	ticketFiles: ["ticketId", "projectId"],
	vehicleDocumentFiles: ["vehicleId"],
	wikiPageFiles: ["wikiPageId"],
} as const satisfies Record<FileCollectionTable, readonly [string, ...string[]]>;

export type FileCollectionKey<T extends FileCollectionTable = FileCollectionTable> =
	(typeof FILE_COLLECTION_KEYS)[T][number];

/**
 * Whether a collection may be addressed by the given column.
 *
 * @param tableName - The registered collection.
 * @param key - The column name a request wants the parent id matched against.
 * @returns Whether the pair is one the registry declares.
 */
export function isFileCollectionKey(tableName: FileCollectionTable, key: string): boolean {
	return (FILE_COLLECTION_KEYS[tableName] as ReadonlyArray<string>).includes(key);
}

/**
 * One target the classifier considered for a file, and how strongly.
 *
 * The inbox offers these as the first entries of a row's destination menu. Without them the page
 * would be "pick from every project in the organisation"; with them it is "the model thought it was
 * one of these three", which is the difference between a screen somebody uses and one they close.
 */
export interface FileClassificationCandidate {
	/** The collection the candidate would be filed into. */
	collection: FileCollectionTable;
	/** The foreign key values that would be written, ready to hand to the filing route. */
	foreignKeyValues: Record<string, string>;
	/** What the row is called, so the entry reads as a project rather than as an id. */
	label: string;
	/** The classifier's own confidence in this candidate, between 0 and 1. */
	confidence: number;
}

/**
 * One file that was dropped onto the window and not filed: the file itself, the sentence saying
 * why, and what the classifier had to choose between.
 *
 * The whole `files` row travels rather than a handful of its columns, because the row is what an
 * unfiled file's write rule is evaluated against — a file linked to nothing is its uploader's and
 * the administrator's, and `createdBy` is the column that says so.
 *
 * The list is driven off the classification's own state rather than off "has no link row".
 * Twenty-one tables reference `files.id` and only fifteen of them are registry junctions, so a
 * sweep for unlinked files would list every workspace logo and every letterhead as something
 * nobody filed.
 */
export interface FileInboxListItem {
	/** The classification's id, which is the row's identity: one classification per file. */
	id: string;
	state: "unplaced" | "failed";
	/** One sentence, from the classifier — why this file is here rather than on a record. */
	reason: string | null;
	error: string | null;
	candidates: FileClassificationCandidate[];
	/**
	 * What the classifier read the document as, or null when it never got that far. `"offer"` is
	 * the one the row acts on: an Angebot or a Leistungsverzeichnis is something a calculation can
	 * be raised from, and the inbox is where that is offered.
	 */
	documentKind: Enums<"documentKind"> | null;
	/** Who dropped it, resolved on the server: an id in a table cell is not a name. */
	uploadedByName: string | null;
	file: Tables<"files">;
}

export type FileInboxList = Array<FileInboxListItem>;

/**
 * One file of one drop, and what became of it — filed or not.
 *
 * Wider than `FileInboxListItem` in the two ways a report is wider than a worklist: the state is
 * every state rather than the two that need attention, and a filed row carries where it went.
 */
export interface FileBatchListItem {
	/** The classification's id, which is the row's identity: one classification per file. */
	id: string;
	state: Enums<"fileClassificationState">;
	/** The classifier's sentence — why it went where it went, or why it went nowhere. */
	reason: string | null;
	error: string | null;
	candidates: FileClassificationCandidate[];
	documentKind: Enums<"documentKind"> | null;
	/**
	 * Where the file was filed, with the record already named: `target_keys` holds an id, and an
	 * id in a table cell is not a place. Null for anything not filed, and `label` is null for a
	 * record the caller cannot reach or that has since been deleted.
	 */
	target: { collection: FileCollectionTable; label: string | null } | null;
	/** Who dropped it, resolved on the server: an id in a table cell is not a name. */
	uploadedByName: string | null;
	file: Tables<"files">;
}

export type FileBatchList = Array<FileBatchListItem>;
